import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Check, Download, LogOut, Trash2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "w8ed_admin_token";

const inputClass =
  "w-full bg-transparent border border-[#e7dfc9] px-4 py-3 font-mono text-xs tracking-[0.15em] text-[#1a1812] placeholder:text-[#b3a67f] focus:outline-none focus:border-[#B08D2E] transition-colors";

const decisionButtonClass =
  "border border-[#e7dfc9] px-3 py-1.5 font-mono text-[9px] tracking-[0.2em] uppercase text-[#6b6455] hover:border-[#B08D2E] hover:text-black transition-colors disabled:opacity-40";

const checkboxClass =
  "appearance-none w-3.5 h-3.5 border border-[#c9bd97] checked:bg-[#B08D2E] cursor-pointer transition-colors";

const STATUS_STYLES = {
  pending: "text-[#8a8272]",
  accepted: "text-[#B08D2E]",
  declined: "text-[#b91c1c] line-through",
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState(null);
  const [busyId, setBusyId] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [pendingDeleteId, setPendingDeleteId] = useState("");

  const fetchEntries = useCallback(
    async (tok) => {
      try {
        const { data } = await axios.get(`${API}/waitlist/entries`, {
          headers: { Authorization: `Bearer ${tok}` },
        });
        setEntries(data);
      } catch (e) {
        if (e?.response?.status === 401 || e?.response?.status === 403) {
          localStorage.removeItem(TOKEN_KEY);
          setToken("");
        } else {
          setError(formatApiErrorDetail(e?.response?.data?.detail));
        }
      }
    },
    []
  );

  useEffect(() => {
    if (token) fetchEntries(token);
  }, [token, fetchEntries]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (e2) {
      setError(formatApiErrorDetail(e2?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    const { data } = await axios.get(`${API}/waitlist/export`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    });
    const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "w8list.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const applyDecision = async (id, status) => {
    const { data } = await axios.post(
      `${API}/waitlist/${id}/status`,
      { status },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return data;
  };

  const handleDecision = async (entryId, status) => {
    setError("");
    setBusyId(entryId);
    try {
      const data = await applyDecision(entryId, status);
      setEntries((prev) =>
        prev ? prev.map((e) => (e.id === entryId ? { ...e, status: data.status } : e)) : prev
      );
      if (!data.email_sent) {
        setError("Decision saved, but the email failed to send.");
      }
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusyId("");
    }
  };

  const handleBatch = async (status) => {
    setError("");
    setBusyId("batch");
    const ids = Array.from(selected);
    let failed = 0;
    let emailFailed = 0;
    const results = await Promise.all(
      ids.map(async (id) => {
        try {
          return await applyDecision(id, status);
        } catch {
          failed += 1;
          return null;
        }
      })
    );
    const byId = {};
    results.filter(Boolean).forEach((r) => {
      byId[r.id] = r;
      if (!r.email_sent) emailFailed += 1;
    });
    setEntries((prev) =>
      prev ? prev.map((e) => (byId[e.id] ? { ...e, status: byId[e.id].status } : e)) : prev
    );
    setSelected(new Set());
    if (failed > 0) setError(`${failed} decision(s) failed. Try again.`);
    else if (emailFailed > 0) setError(`Saved, but ${emailFailed} email(s) failed to send.`);
    setBusyId("");
  };

  const handleDelete = async (entryId) => {
    if (pendingDeleteId !== entryId) {
      setPendingDeleteId(entryId);
      return;
    }
    setPendingDeleteId("");
    setBusyId(entryId);
    try {
      await axios.delete(`${API}/waitlist/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEntries((prev) => (prev ? prev.filter((e) => e.id !== entryId) : prev));
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(entryId);
        return next;
      });
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusyId("");
    }
  };

  const toggleSelect = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allSelected =
    !!entries && entries.length > 0 && entries.every((e) => selected.has(e.id));

  const toggleAll = () => {
    if (!entries) return;
    setSelected(allSelected ? new Set() : new Set(entries.map((e) => e.id)));
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setEntries(null);
  };

  if (!token) {
    return (
      <main
        data-testid="admin-login-page"
        className="min-h-screen bg-[#faf8f4] flex items-center justify-center px-6"
      >
        <form
          onSubmit={handleLogin}
          data-testid="admin-login-form"
          className="w-full max-w-sm bg-white border border-[#e7dfc9] p-8"
        >
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#a08a4f] mb-2">
            W8ED
          </p>
          <h1 className="text-xl font-semibold tracking-wide text-black mb-6">
            ADMIN SIGN IN
          </h1>
          <div className="space-y-4">
            <input
              type="email"
              required
              data-testid="admin-email-input"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
            />
            <input
              type="password"
              required
              data-testid="admin-password-input"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClass}
            />
            {error && (
              <p
                data-testid="admin-login-error"
                className="font-mono text-[10px] tracking-[0.15em] uppercase text-red-700"
              >
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="w-full bg-[#0a0a0a] text-white font-mono text-[11px] tracking-[0.3em] uppercase py-3.5 hover:bg-[#B08D2E] transition-colors disabled:opacity-50"
            >
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </div>
          <Link
            to="/"
            data-testid="admin-back-link"
            className="block mt-6 text-center font-mono text-[10px] tracking-[0.3em] uppercase text-[#b3a67f] hover:text-[#B08D2E] transition-colors"
          >
            Back to site
          </Link>
        </form>
      </main>
    );
  }

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-[#faf8f4] px-6 py-10 sm:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#a08a4f] mb-2">
              W8ED Admin
            </p>
            <h1 className="text-2xl font-semibold tracking-wide text-black">THE W8LIST</h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {selected.size > 0 && (
              <>
                <button
                  type="button"
                  data-testid="batch-accept-button"
                  disabled={busyId === "batch"}
                  onClick={() => handleBatch("accepted")}
                  className="flex items-center gap-2 border border-[#B08D2E] font-mono text-[10px] tracking-[0.2em] uppercase text-[#B08D2E] px-4 py-2.5 hover:border-[#8f7323] transition-colors disabled:opacity-40"
                >
                  <Check size={13} /> Accept {selected.size}
                </button>
                <button
                  type="button"
                  data-testid="batch-decline-button"
                  disabled={busyId === "batch"}
                  onClick={() => handleBatch("declined")}
                  className="flex items-center gap-2 border border-[#e7dfc9] font-mono text-[10px] tracking-[0.2em] uppercase text-[#6b6455] px-4 py-2.5 hover:border-red-300 hover:text-red-700 transition-colors disabled:opacity-40"
                >
                  <X size={13} /> Decline {selected.size}
                </button>
              </>
            )}
            <span
              data-testid="admin-count"
              className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#6b6455] border border-[#e7dfc9] px-4 py-2.5"
            >
              {entries ? `${entries.length} signed up` : "Loading..."}
            </span>
            <button
              type="button"
              data-testid="export-csv-button"
              onClick={handleExport}
              className="flex items-center gap-2 bg-[#0a0a0a] text-white font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-2.5 hover:bg-[#B08D2E] transition-colors"
            >
              <Download size={13} /> Export CSV
            </button>
            <button
              type="button"
              data-testid="admin-logout-button"
              onClick={handleLogout}
              className="flex items-center gap-2 border border-[#e7dfc9] font-mono text-[10px] tracking-[0.2em] uppercase text-[#6b6455] px-4 py-2.5 hover:border-[#B08D2E] hover:text-black transition-colors"
            >
              <LogOut size={13} /> Log out
            </button>
          </div>
        </div>

        {error && (
          <p data-testid="admin-error" className="mb-4 font-mono text-[10px] tracking-[0.15em] uppercase text-red-700">
            {error}
          </p>
        )}

        <div className="border border-[#e7dfc9] bg-white overflow-x-auto">
          <table data-testid="waitlist-table" className="w-full text-left">
            <thead>
              <tr className="border-b border-[#e7dfc9]">
                <th className="px-4 py-3">
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    data-testid="select-all-checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    className={checkboxClass}
                  />
                </th>
                {["Email", "Name", "Phone", "Country", "City", "Interest", "Training", "Obstacle", "Status", "Joined", "Decision"].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 font-mono text-[9px] tracking-[0.25em] uppercase text-[#a08a4f] whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries && entries.length === 0 && (
                <tr>
                  <td
                    colSpan={12}
                    className="px-4 py-10 text-center font-mono text-[11px] tracking-[0.2em] uppercase text-[#b3a67f]"
                  >
                    No signups yet
                  </td>
                </tr>
              )}
              {entries &&
                entries.map((entry) => {
                  const status = entry.status || "pending";
                  return (
                    <tr
                      key={entry.id}
                      data-testid="waitlist-row"
                      className="border-b border-[#f0ead6] hover:bg-[#faf6ea] transition-colors"
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          aria-label={`Select ${entry.email}`}
                          data-testid={`select-row-${entry.id}`}
                          checked={selected.has(entry.id)}
                          onChange={() => toggleSelect(entry.id)}
                          className={checkboxClass}
                        />
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-[#1a1812]">{entry.email}</td>
                      <td className="px-4 py-3 font-mono text-xs text-[#6b6455]">{entry.full_name || "—"}</td>
                      <td className="px-4 py-3 font-mono text-xs text-[#6b6455]">{entry.phone || "—"}</td>
                      <td className="px-4 py-3 font-mono text-xs text-[#6b6455]">{entry.country || "—"}</td>
                      <td className="px-4 py-3 font-mono text-xs text-[#6b6455]">{entry.city || "—"}</td>
                      <td className="px-4 py-3 font-mono text-xs text-[#6b6455]">{entry.interest || "—"}</td>
                      <td className="px-4 py-3 font-mono text-xs text-[#6b6455]">{entry.training_frequency || "—"}</td>
                      <td className="px-4 py-3 font-mono text-xs text-[#6b6455]">{entry.obstacle || "—"}</td>
                      <td className="px-4 py-3">
                        <span
                          data-testid={`status-cell-${entry.id}`}
                          className={`font-mono text-[10px] tracking-[0.2em] uppercase ${STATUS_STYLES[status] || STATUS_STYLES.pending}`}
                        >
                          {status}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-[#a08a4f] whitespace-nowrap">
                        {entry.created_at ? new Date(entry.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {status !== "accepted" && (
                            <button
                              type="button"
                              data-testid={`accept-button-${entry.id}`}
                              disabled={busyId === entry.id || busyId === "batch"}
                              onClick={() => handleDecision(entry.id, "accepted")}
                              className={decisionButtonClass}
                            >
                              <span className="flex items-center gap-1.5">
                                <Check size={11} /> Accept
                              </span>
                            </button>
                          )}
                          {status !== "declined" && (
                            <button
                              type="button"
                              data-testid={`decline-button-${entry.id}`}
                              disabled={busyId === entry.id || busyId === "batch"}
                              onClick={() => handleDecision(entry.id, "declined")}
                              className={decisionButtonClass}
                            >
                              <span className="flex items-center gap-1.5">
                                <X size={11} /> Decline
                              </span>
                            </button>
                          )}
                          <button
                            type="button"
                            aria-label={`Delete ${entry.email}`}
                            data-testid={`delete-button-${entry.id}`}
                            disabled={busyId === entry.id}
                            onClick={() => handleDelete(entry.id)}
                            className={`px-2 py-1.5 transition-colors disabled:opacity-40 ${
                              pendingDeleteId === entry.id
                                ? "font-mono text-[9px] tracking-[0.2em] uppercase text-red-700 border border-red-300"
                                : "text-[#c9bd97] hover:text-red-700"
                            }`}
                          >
                            {pendingDeleteId === entry.id ? "Sure?" : <Trash2 size={13} />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </main>
  );
}
